import React from "react";
import { Modal, Button, Stack, Group, Box, ScrollArea, ActionIcon, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IDocxTemplate } from "@mctypes/global.types";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import { ConfirmDialog, HSpace, VSpace } from "../";
import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";
import { formatShortDate } from "../../app/routes/admin/selectors";
import NewDocxUpload from "./NewDocxUpload/NewDocxUpload";

type Props = {
  docxTemplates: IDocxTemplate[];
  uploadFunc: (event: React.ChangeEvent<HTMLInputElement>, level?: string) => void;
  uploadCompleedCb: () => void;
  generateDocx: (docxTemplate: IDocxTemplate) => void;
  downloadTemplate: (docxTemplate: IDocxTemplate) => void;
  deleteDocx: (docxTemplate: IDocxTemplate) => void;
  fetchAndSet?: () => void;
  closeModal: () => void;
  demoDocxData: any;
};

const DocxReport: React.FC<Props> = ({
  docxTemplates,
  uploadFunc,
  uploadCompleedCb,
  generateDocx,
  downloadTemplate,
  deleteDocx,
  fetchAndSet,
  closeModal,
  demoDocxData,
}) => {
  const isNarrow = useMediaQuery("(max-width: 1200px)");
  const isTiny = useMediaQuery("(max-width: 720px)");

  const deleteFile = async (docxTemplate: IDocxTemplate) => {
    await deleteDocx(docxTemplate);
    fetchAndSet?.();
  };

  const upload = async (event: React.ChangeEvent<HTMLInputElement>, level?: string) => {
    await uploadFunc(event, level);
    fetchAndSet?.();
  };

  return (
    <Modal
      opened
      onClose={closeModal}
      title="Rapportmaler"
      centered
      radius="lg"
      size={isNarrow ? "90vw" : 1100} // <-- större modal
      fullScreen={isTiny} // <-- fullskärm på små skärmar
      withinPortal
    >
      <ScrollArea.Autosize mah="70vh" type="always" offsetScrollbars>
        <Stack gap="lg">
          {demoDocxData != null && (
            <>
              <NewDocxUpload
                demoData={demoDocxData}
                uploadFunc={upload}
                uploadCompleedCb={uploadCompleedCb}
                docxTemplates={docxTemplates}
              />
              <VSpace />
              <DocxTable
                docxTemplates={docxTemplates}
                downloadFile={downloadTemplate}
                generateReport={generateDocx}
                deleteFile={deleteFile}
              />
            </>
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Modal>
  );
};

export default DocxReport;

const DocxTable: React.FC<{
  docxTemplates: IDocxTemplate[];
  downloadFile: (docxTemplate: IDocxTemplate) => void;
  generateReport: (docxTemplate: IDocxTemplate) => void;
  deleteFile: (docxTemplate: IDocxTemplate) => void;
}> = ({ docxTemplates, downloadFile, generateReport, deleteFile }) => {
  const columns: ColDef<IDocxTemplate>[] = [
    { field: "_id", headerName: "", hide: true },
    { field: "isMulticonsult", headerName: "", hide: true },
    { field: "fileName", headerName: "Navn", flex: 1, minWidth: 220 },
    {
      field: "uploadedDate",
      headerName: "Opprettet",
      width: 140,
      cellRenderer: (p: any) => formatShortDate(p.value),
    },
    {
      field: "uploadedBy.name",
      headerName: "Opplastet av",
      width: 180,
      cellRenderer: (p: any) => <span>{p.value == null ? "Multiconsult" : p.value}</span>,
    },
    {
      field: "templateLevel",
      headerName: "Nivå",
      width: 120,
      cellRenderer: (p: any) => (p.value === "BOARD" ? "Register" : p.value === "PROJECT" ? "Prosjekt" : null),
    },
    {
      field: "_id",
      headerName: "",
      width: 150,
      cellStyle: { textAlign: "center", padding: 0 },
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        const row: IDocxTemplate = params.data;
        return (
          <Group gap={4} justify="center" wrap="nowrap">
            <IconBtn label="Last ned mal" onClick={() => downloadFile(row)}>
              <i className="fa fa-download" />
            </IconBtn>

            <IconBtn label="Test med demodata" onClick={() => generateReport(row)}>
              <i className="fa fa-play" />
            </IconBtn>

            {row.isMulticonsult === false && (
              <ConfirmDialog
                title="Slette mal?"
                message="Er du sikker på at du vil slette malen?"
                confirmCb={() => deleteFile(row)}
              >
                <IconBtn label="Slett" color="red" onClick={() => {}}>
                  <i className="fa fa-trash" />
                </IconBtn>
              </ConfirmDialog>
            )}
          </Group>
        );
      },
    },
  ];

  return (
    <Box className="ag-theme-quartz w-100">
      <AgGridReact
        domLayout="autoHeight"
        rowData={docxTemplates}
        defaultColDef={{ suppressHeaderMenuButton: true, cellClass: "d-block" }}
        headerHeight={35}
        columnDefs={columns}
        suppressClickEdit
        animateRows={false}
        suppressContextMenu
        localeText={AG_GRID_LOCALE_NOR}
      />
    </Box>
  );
};

// liten helper så vi slipper repetera stopp av click/keydown
const IconBtn: React.FC<React.PropsWithChildren<{ onClick: () => void; color?: string; label: string }>> = ({
  onClick,
  color,
  label,
  children,
}) => (
  <Tooltip label={label} withArrow>
    <ActionIcon
      variant="subtle" // subtil (ingen kant)
      color={color} // "red" för delete
      size="md" // kompakt
      radius="xl" // rund
      p={6} // tight padding
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      aria-label={label}
    >
      {children}
    </ActionIcon>
  </Tooltip>
);
